import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import config from 'config'
import querystring from 'querystring'
import Font from '@cred/react-font'
import { extractNumber } from '@cred/shared/utils'
import modalActions from '@cred/react-modal/actions'
import CtaButton from 'components/cta-button'
import PoweredBy from 'components/powered-by'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import LoanAmountForm, { amountIsValid } from './loan-amount-form'
import SavingsDisplay from './savings-display'
import { SAVINGS_CALCULATOR_REDUX_FORM_NAME, SAVINGS_MULTIPLIER, LOAN_AMOUNT_FIELD_NAME } from './constants'
import { savingsCalculatorContext } from './analytics'
import styles from './styles.module.scss'

const generateHref = ({ utms, amount, invalidAmount }) => {
  if (invalidAmount) return ''

  let loanAmountQuery = ''
  if (amount) {
    loanAmountQuery = `loanAmount=${extractNumber(amount)}&`
  }

  return `${config.credibleRoot}/personal-loans/borrower?${loanAmountQuery}${querystring.encode(utms)}`
}

const generateHandleSubmit = ({ utms, amount, invalidAmount }) => e => {
  e.preventDefault()
  const personalLoansUrl = generateHref({ utms, amount, invalidAmount })
  if (personalLoansUrl) {
    window.open(personalLoansUrl)
  }
}

const ChevronSection = () => (
  <div className={styles.chevronContainer}>
    <Font
      type="medium"
      weight="bold"
      className="cred-icon icon-chevron-right hidden-xs"
      element="i"
      color="primary"
    />
    <Font
      type="medium"
      weight="bold"
      className="cred-icon icon-chevron-down hidden-sm hidden-md hidden-lg hidden-xl"
      element="i"
      color="primary"
    />
  </div>
)

const SavingsCalculator = ({ utms, amount, savings, invalidAmount, theme }) => (
  <div className={styles.savingsCalculatorRoot}>
    <AdvertiserDisclosure tooltipId="calculator-disclosure" theme={theme} />
    <form
      id="personal-loans-savings-calculator"
      className={styles.savingsCalculatorMain}
      onSubmit={generateHandleSubmit({ utms, amount, invalidAmount })}
    >
      <Font
        className={`${styles.header} align-center mb-16 mb-sm-24`}
        color="primary"
        type="heading"
        element="h2"
        weight="bold"
      >
        You could save money with a personal loan
      </Font>
      <section className={styles.contentWrapper}>
        <LoanAmountForm />
        <ChevronSection />
        <SavingsDisplay savings={savings} />
      </section>
      <section className={`${styles.footerWrapper}`}>
        <CtaButton
          className={styles.ctaButton}
          disabled={invalidAmount}
          analyticsContexts={savingsCalculatorContext}
          type="submit"
        >
          Find My Rates
        </CtaButton>
        <PoweredBy className="mt-8" />
      </section>
    </form>
  </div>
)

SavingsCalculator.propTypes = {
  utms: PropTypes.object,
  amount: PropTypes.string,
  savings: PropTypes.number,
  invalidAmount: PropTypes.bool,
  theme: PropTypes.string,
}

const calculateSavingsFromAmount = amount => {
  if (amountIsValid(amount)) {
    return SAVINGS_MULTIPLIER * extractNumber(amount)
  }

  return null
}

const mapDispatchToProps = dispatch => ({ actions: bindActionCreators(modalActions, dispatch) })
const mapStateToProps = ({ form }) => {
  const calculatorForm = form[SAVINGS_CALCULATOR_REDUX_FORM_NAME],
    calculatorFormValue = calculatorForm && calculatorForm.values ? calculatorForm.values : {},
    amount = calculatorFormValue[LOAN_AMOUNT_FIELD_NAME] || null
  return {
    amount,
    savings: calculateSavingsFromAmount(amount),
    invalidAmount: calculatorForm && calculatorForm.syncErrors && !!calculatorForm.syncErrors[LOAN_AMOUNT_FIELD_NAME],
  }
}

export {
  SavingsCalculator,
  mapStateToProps,
  calculateSavingsFromAmount,
  ChevronSection,
  generateHref,
}

export default connect(mapStateToProps, mapDispatchToProps)(SavingsCalculator)
